import React, { useEffect, useState } from 'react';
import { Box, Typography, Tooltip, LinearProgress, Stack, FormControl, Select, MenuItem } from '@mui/material';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { feature } from 'topojson-client';
import { scaleQuantize } from 'd3-scale';
import { fetchVisitorStats } from '../../api/analyticsApi';
import countries from 'i18n-iso-countries';
import worldAtlasData from 'world-atlas/countries-110m.json';
import numericToAlpha3 from '../../utils/numericToAlpha3';
countries.registerLocale(require('i18n-iso-countries/langs/en.json'));

const getCountryName = (iso3) =>
  countries.getName(iso3, 'en', { select: 'official' }) || iso3;


const WorldMapChart = () => {
  const [visitorData, setVisitorData] = useState({});
  const [geoData, setGeoData] = useState(null);
  const [period, setPeriod] = useState(() => localStorage.getItem('worldmapPeriod') || '60d');

  useEffect(() => {
    const objectName = Object.keys(worldAtlasData.objects)[0];
    const geo = feature(worldAtlasData, worldAtlasData.objects[objectName]);
    setGeoData(geo);

    const loadData = async () => {
      const user = JSON.parse(localStorage.getItem('user'));
      const propertyId = user?.propertyId?.id;
      console.log("📡 Fetching visitor stats with:", { propertyId, period });
      const stats = await fetchVisitorStats(propertyId, period);
      const mapped = {};
      stats.forEach(item => {
        const alpha3 = countries.alpha2ToAlpha3(item._id);
        if (alpha3) {
          mapped[alpha3] = { count: item.count, trend: item.trend || 0 };
        }
      });
      setVisitorData(mapped);
    };

    loadData();
    const interval = setInterval(loadData, 10000);
    return () => clearInterval(interval);
  }, [period]);

  const maxVisitors = Math.max(...Object.values(visitorData).map(v => v.count));
  const colorScale = scaleQuantize()
    .domain([0, maxVisitors])
    .range([
      '#dceefb',
      '#b6e0fe',
      '#84c5f4',
      '#62b0e8',
      '#4098d7',
      '#2680c2',
      '#186faf',
      '#0f609b',
      '#0a558c',
    ]);
  return (
    <Box>
      <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1, ml: 0.5 }}>
        WHERE ARE YOUR USERS VISITING FROM?
      </Typography>
      <Box
        sx={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(60,64,67,0.15)',
          p: 3,
        }}
      >
    
      <Box
        height={400}
        width="100%"
        bgcolor="#ffffff"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        overflow="hidden"
        px={2}
      >
        <Box flex="2" display="flex" justifyContent="center">
          <ComposableMap projectionConfig={{ scale: 190 }}>
            {geoData && (
              <Geographies geography={geoData}>
                {({ geographies }) => {
                  return geographies.map((geo) => {
                    const isoCode = numericToAlpha3[geo.id];
                    const visitor = isoCode ? visitorData[isoCode] : undefined;
                   
                    return (
                      <Tooltip
                        key={geo.rsmKey}
                        title={
                          <Box display="flex" alignItems="center" gap={1}>
                            {countries.alpha3ToAlpha2(isoCode) && (
                              <img
                                src={`https://flagcdn.com/w40/${countries.alpha3ToAlpha2(isoCode).toLowerCase()}.png`}
                                alt={isoCode}
                                style={{ width: 20, height: 14, borderRadius: 2 }}
                              />
                            )}
                            <span>{getCountryName(isoCode)}: {visitor?.count || 0} visitors</span>
                          </Box>
                        }
                        arrow
                        placement="top"
                      >
                        <g>
                          <Geography
                            geography={geo}
                            style={{
                              default: {
                                fill: visitor ? colorScale(visitor.count) : '#ECEFF1',
                                stroke: '#607D8B',
                                strokeWidth: 0.5,
                              },
                              hover: { fill: '#90CAF9' },
                              pressed: { fill: '#42A5F5' },
                            }}
                          />
                        </g>
                      </Tooltip>
                    );
                  });
                }}
              </Geographies>
            )}
          </ComposableMap>
        </Box>
        <Box
          flex="1.5"
          pl={1}
          display="flex"
          flexDirection="column"
          gap={1}
          sx={{ maxHeight: 380, overflowY: 'auto', pr: 1 }}
        >
          
          <Box display="flex" justifyContent="space-between" alignItems="center"  pb={1}>
            <Typography variant="body2" fontWeight="bold" color="textSecondary">
              COUNTRY
            </Typography>
            <Typography variant="body2" fontWeight="bold" color="textSecondary">
              USERS
            </Typography>
          </Box>
          {Object.entries(visitorData)
            .sort((a, b) => b[1].count - a[1].count)
            .map(([code, data]) => {
              const progress = Math.min((data.count / maxVisitors) * 100, 100);
              const alpha2 = countries.alpha3ToAlpha2(code)?.toLowerCase();
              const flagUrl = alpha2 ? `https://flagcdn.com/w40/${alpha2}.png` : null;
              return (
                <Stack key={code} spacing={0.5}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" gap={1}>
                      {flagUrl && (
                        <img
                          src={flagUrl}
                          alt={code}
                          style={{ width: 24, height: 16, borderRadius: 2 }}
                        />
                      )}
                      <Typography variant="body2" fontWeight="medium">
                        {getCountryName(code)}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" fontWeight="bold">
                        {data.count.toLocaleString()} 
                      </Typography>
                      <Typography
                        variant="body2"
                        color={data.trend >= 0 ? 'success.main' : 'error.main'}
                      >
                        {data.trend >= 0 ? '▲' : '▼'} {Math.abs(data.trend)}%
                      </Typography>
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Stack>
              );
            })}
        </Box>
      </Box>
      <Box mt={2} display="flex" justifyContent="flex-start">
        <FormControl size="small">
          <Select
            value={period}
            onChange={(e) => {
              const value = e.target.value;
              setPeriod(value);
              localStorage.setItem('worldmapPeriod', value);
            }}
            variant="standard"
            disableUnderline
          >
            <MenuItem value="7d">Last 7 days</MenuItem>
            <MenuItem value="30d">Last 30 days</MenuItem>
            <MenuItem value="60d">Last 60 days</MenuItem>
            <MenuItem value="90d">Last 90 days</MenuItem>
          </Select>
        </FormControl>
      </Box>
      </Box>
    </Box>
  );
};

export default WorldMapChart;
